import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Switch from "react-switch";
import Select, { StylesConfig } from "react-select";
import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";
import CloseIcon from "../../../../public/images/close-icon-dark.svg";
import styles from "./dialogs.module.scss";
import {
  ScheduleResourceResponse,
  ScheduleResourcePayload,
  ResourceCategoryResponse,
} from "app/utils/request/scheduling/facilityResources";
import { resourceNameTaken } from "./resourceName";

type Option = { value: number; label: string };

// Matches the react-select styling of `ResourceSelector` (Figma 84:27616,
// 134:15505), which the booking option modals already use. The section
// modal does not use a select, so only this file holds these styles. The hex
// values name the `booking-tokens` token they match; react-select styles are
// a JS object, so they cannot read the SCSS tokens directly.
export const selectStyles: StylesConfig = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: "48px",
    height: "48px",
    background: "#fff", // $cn-surface
    border: state.isFocused ? "1px solid #039be5" : "1px solid #e7e7e7", // $cn-blue / $cn-line
    borderRadius: "12px", // $cn-radius-control
    boxShadow: "none",
    ":hover": {
      border: state.isFocused ? "1px solid #039be5" : "1px solid #e7e7e7",
    },
  }),
  valueContainer: (base: any) => ({
    ...base,
    height: "48px",
    padding: "0 16px",
  }),
  indicatorsContainer: (base: any) => ({ ...base, height: "48px" }),
  singleValue: (base: any) => ({ ...base, color: "#000", fontSize: "16px" }), // $cn-black
  placeholder: (base: any) => ({
    ...base,
    color: "#8c8c8c", // $cn-muted
    fontSize: "16px",
  }),
  container: (base: any) => ({ ...base, color: "#8c8c8c" }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base: any) => ({
    ...base,
    color: "#8c8c8c", // $cn-muted
    padding: "0 16px 0 0",
    svg: { width: 24, height: 24 },
  }),
  menu: (base: any) => ({
    ...base,
    zIndex: 20,
    borderRadius: "16px",
    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.08)",
    border: "none",
  }),
  option: (base: any, state: any) => ({
    ...base,
    padding: "10px 16px",
    fontSize: "16px",
    lineHeight: "24px",
    color: "#000",
    backgroundColor:
      state.isFocused || state.isSelected
        ? "rgba(3, 155, 229, 0.1)" // $cn-blue tint
        : "#fff",
    cursor: "pointer",
  }),
};

interface ResourceModalProps {
  isOpen: boolean;
  resource: ScheduleResourceResponse | null;
  categories: ResourceCategoryResponse[];
  // The resources of the facility, for the unique name check in a section.
  resources: ScheduleResourceResponse[];
  onClose: () => void;
  onSave: (payload: ScheduleResourcePayload) => Promise<void>;
}

const ResourceModal: React.FC<ResourceModalProps> = ({
  isOpen,
  resource,
  categories,
  resources,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();

  const [title, setTitle] = useState("");
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [active, setActive] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Fills the form each time the page opens the modal, so an edit starts from
  // the resource and an add starts empty.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setTitle(resource?.title ?? "");
    setCategoryId(resource?.resourceCategoryId ?? null);
    setActive(resource?.active ?? true);
  }, [isOpen, resource]);

  const isDuplicate = resourceNameTaken(
    title,
    categoryId,
    resources,
    resource?.id
  );
  const isValid =
    title.trim().length > 0 && categoryId !== null && !isDuplicate;

  const sectionOptions: Option[] = categories.map((category) => ({
    value: category.id,
    label: category.title,
  }));

  const handleSave = async () => {
    if (!isValid || categoryId === null) {
      return;
    }

    setIsSaving(true);
    try {
      await onSave({
        title: title.trim(),
        resource_category_id: categoryId,
        active,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ModalLayout isOpen={isOpen} onClose={onClose} classNames={styles.modal}>
      <div className={styles.modal_header}>
        <h2>{resource ? t("Edit Resource") : t("Add Resource")}</h2>
        <button
          type="button"
          className={styles.modal_close}
          onClick={onClose}
          aria-label={t("Close")}
        >
          <img src={CloseIcon} alt="" />
        </button>
      </div>

      <div className={styles.modal_content}>
        <div className={styles.form_row}>
          <label htmlFor="resource-name">{t("Name")}</label>
          <input
            id="resource-name"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          {isDuplicate && (
            <div className={styles.field_error}>
              {t("This section already has a resource with that name.")}
            </div>
          )}
        </div>

        <div className={styles.form_row}>
          <label htmlFor="resource-section">{t("Section")}</label>
          <Select
            inputId="resource-section"
            styles={selectStyles}
            options={sectionOptions}
            value={
              sectionOptions.find((option) => option.value === categoryId) ??
              null
            }
            onChange={(option: any) => setCategoryId(option?.value ?? null)}
            placeholder={t("Select a section")}
            noOptionsMessage={() => t("Add a section first")}
            menuPlacement="auto"
          />
        </div>

        <div className={styles.form_row_inline}>
          <Switch
            checked={active}
            onChange={setActive}
            offColor="#CECECE"
            onColor="#039be5"
            onHandleColor="#FFFFFF"
            handleDiameter={27}
            uncheckedIcon={false}
            checkedIcon={false}
            height={31}
            width={51}
          />
          <div>
            <div className={styles.inline_label}>{t("Active")}</div>
            <div className={styles.inline_hint}>
              {t(
                "Inactive resources can't be booked or linked to booking options."
              )}
            </div>
          </div>
        </div>

        <div className={styles.button_group}>
          <button
            type="button"
            className={styles.secondary_button}
            onClick={onClose}
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            className={styles.primary_button}
            disabled={!isValid || isSaving}
            onClick={handleSave}
          >
            {resource ? t("Save Resource") : t("Add Resource")}
          </button>
        </div>
      </div>
    </ModalLayout>
  );
};

export default ResourceModal;
